/**
 * Time zone helpers built on Intl, so they work the same in the browser, the Android
 * WebView and Cloudflare Workers without bundling tz data.
 *
 * Conventions: an instant is a number of UTC milliseconds. A local date is 'YYYY-MM-DD'.
 * A local date-time is 'YYYY-MM-DDTHH:mm'. Zones are IANA names like 'America/New_York'.
 */

export interface LocalParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number;
  second: number;
  /** 0 = Sunday ... 6 = Saturday */
  weekday: number;
}

const DAY_MS = 86_400_000;
const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = partsFormatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    partsFormatters.set(tz, f);
  }
  return f;
}

export function isValidTimeZone(tz: string): boolean {
  try {
    partsFormatter(tz);
    return true;
  } catch {
    return false;
  }
}

/** Wall-clock parts of an instant in a zone. */
export function localParts(ms: number, tz: string): LocalParts {
  const out: Partial<Record<string, string>> = {};
  for (const p of partsFormatter(tz).formatToParts(ms)) out[p.type] = p.value;
  let hour = Number(out.hour);
  if (hour === 24) hour = 0;
  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour,
    minute: Number(out.minute),
    second: Number(out.second),
    weekday: WEEKDAYS[out.weekday ?? 'Sun'] ?? 0,
  };
}

/** Offset of a zone from UTC at an instant, in minutes (New York in summer is -240). */
export function tzOffsetMinutes(ms: number, tz: string): number {
  const whole = Math.floor(ms / 1000) * 1000;
  const p = localParts(whole, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - whole) / 60_000);
}

export type Disambiguation = 'compatible' | 'earlier' | 'later';

/**
 * Converts a wall-clock time in a zone to an instant.
 * Times that occur twice (clocks fall back) resolve to the earlier one by default.
 * Times that don't exist (clocks spring forward) move forward by the gap, like Temporal.
 */
export function zonedToInstant(
  local: { year: number; month: number; day: number; hour?: number; minute?: number },
  tz: string,
  disambiguation: Disambiguation = 'compatible',
): number {
  const wall = Date.UTC(local.year, local.month - 1, local.day, local.hour ?? 0, local.minute ?? 0);
  const offBefore = tzOffsetMinutes(wall - DAY_MS, tz) * 60_000;
  const offAfter = tzOffsetMinutes(wall + DAY_MS, tz) * 60_000;
  const a = wall - offBefore;
  const b = wall - offAfter;
  const aValid = tzOffsetMinutes(a, tz) * 60_000 === offBefore;
  const bValid = tzOffsetMinutes(b, tz) * 60_000 === offAfter;
  if (aValid && bValid) {
    if (a === b) return a;
    const earlier = Math.min(a, b);
    const later = Math.max(a, b);
    return disambiguation === 'later' ? later : earlier;
  }
  if (aValid) return a;
  if (bValid) return b;
  // In a gap: interpret with the offset in force before the transition, which lands after it.
  return disambiguation === 'earlier' ? b : a;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' for an instant in a zone. */
export function localDate(ms: number, tz: string): string {
  const p = localParts(ms, tz);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** 'YYYY-MM-DDTHH:mm' for an instant in a zone. */
export function localDateTime(ms: number, tz: string): string {
  const p = localParts(ms, tz);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** 'HH:mm' (24-hour) for an instant in a zone. */
export function localTime(ms: number, tz: string): string {
  const p = localParts(ms, tz);
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** Parses 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm'. Throws on anything else. */
export function parseLocal(value: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
} {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/.exec(value.trim());
  if (!m) throw new Error(`Not a local date or date-time: ${value}`);
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: m[4] ? Number(m[4]) : 0,
    minute: m[5] ? Number(m[5]) : 0,
  };
}

/** Instant for a local 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm' in a zone. */
export function instantFromLocal(value: string, tz: string, d?: Disambiguation): number {
  return zonedToInstant(parseLocal(value), tz, d);
}

/** Instant of local midnight at the start of the day containing `ms`. */
export function startOfLocalDay(ms: number, tz: string): number {
  return instantFromLocal(localDate(ms, tz), tz);
}

// ---- Calendar date arithmetic on 'YYYY-MM-DD' strings (no zone involved) ----

function dateToUtcMs(date: string): number {
  const p = parseLocal(date);
  return Date.UTC(p.year, p.month - 1, p.day);
}

function utcMsToDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

export function addDays(date: string, days: number): string {
  return utcMsToDate(dateToUtcMs(date) + days * DAY_MS);
}

/** Whole days from a to b (b - a). */
export function diffDays(a: string, b: string): number {
  return Math.round((dateToUtcMs(b) - dateToUtcMs(a)) / DAY_MS);
}

/** 0 = Sunday ... 6 = Saturday */
export function weekdayOf(date: string): number {
  return new Date(dateToUtcMs(date)).getUTCDay();
}

/** Every date from start to end, inclusive. */
export function datesBetween(start: string, end: string): string[] {
  const out: string[] = [];
  const n = diffDays(start, end);
  for (let i = 0; i <= n; i++) out.push(addDays(start, i));
  return out;
}

export function compareDates(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function minDate(a: string, b: string): string {
  return a <= b ? a : b;
}

export function maxDate(a: string, b: string): string {
  return a >= b ? a : b;
}

/** Short zone name like 'EDT', or 'GMT+2' where the zone has no common abbreviation. */
const abbrFormatters = new Map<string, Intl.DateTimeFormat>();
/** Zones whose letters no English locale knows; otherwise they'd read "GMT+9". */
const ABBR_FALLBACK: Record<string, string> = {
  'Asia/Tokyo': 'JST',
  'Asia/Seoul': 'KST',
  'Asia/Shanghai': 'CST',
  'Asia/Hong_Kong': 'HKT',
  'Asia/Taipei': 'CST',
  'Asia/Singapore': 'SGT',
  'Asia/Kuala_Lumpur': 'MYT',
  'Asia/Bangkok': 'ICT',
  'Asia/Ho_Chi_Minh': 'ICT',
  'Asia/Manila': 'PHT',
  'Asia/Jakarta': 'WIB',
  'Asia/Bali': 'WITA',
  'Asia/Makassar': 'WITA',
};

/**
 * The zone's short name at that moment ("CDT", "BST", "CEST", "AEDT"). US English only knows
 * American zones, so other English locales fill in Europe, the Gulf, India and Australia.
 */
export function tzAbbr(ms: number, tz: string): string {
  const generic = (v: string) => /^(GMT|UTC)([+-]|$)/.test(v);
  let first: string | null = null;
  for (const loc of ['en-US', 'en-GB', 'en-AU', 'en-IN']) {
    const key = `${loc}|${tz}`;
    let f = abbrFormatters.get(key);
    if (!f) {
      f = new Intl.DateTimeFormat(loc, { timeZone: tz, timeZoneName: 'short' });
      abbrFormatters.set(key, f);
    }
    const v = f.formatToParts(ms).find((p) => p.type === 'timeZoneName')?.value ?? tz;
    first ??= v;
    if (!generic(v)) return v;
  }
  return ABBR_FALLBACK[tz] ?? first ?? tz;
}

export const MINUTE = 60_000;
export const HOUR = 3_600_000;
export const DAY = DAY_MS;
